import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../Button';
import Icon from '../Icon';
import cards from '../../assets/svg/tile/cards.svg';
import { useDownloadDocQuery } from '../../hooks/query/useDownloadDocQuery';
import downloadIcon from '../../assets/svg/tile/dowload.svg';
import { ReactComponent as PrintIcon } from '../../assets/icons/svg/shared/ic-shared-print.svg';
import { ReactComponent as ExcelIcon } from '../../assets/icons/svg/shared/ic-shared-excel.svg';
import { ReactComponent as PDFIcon } from '../../assets/icons/svg/shared/ic-shared-pdf.svg';

import PdfPeek from './PdfPeek';
import { useDownloadExpenseReport } from '../../hooks/query/useDownloadExpenseReport';
import { IExpenseObject, IUncategorizedTransactions } from '../../types';
import { TILE_CONSTANTS } from './constans';
import { useStore } from '../../store';
import { VIEWS_MAP } from '../../router/constants';

const {
  DOC_TYPE: { STATEMENTS, EXPENSES },
} = TILE_CONSTANTS;

interface IProps {
  statementObj?: any;
  expenseObj?: IExpenseObject;
  uncategorizedTransactions?: IUncategorizedTransactions;
  id: string;
  tileIndex: number;
}

const Tile = ({ statementObj, expenseObj, uncategorizedTransactions, id, tileIndex }: IProps) => {
  const SAVING_BUCKET = 'SAVING_BUCKET';
  const EMERGENCY_BUCKET = 'EMERGENCY_BUCKET';
  const TAX_BUCKET = 'TAX_BUCKET';
  const MAIN_ACCOUNT = 'MAIN_ACCOUNT';
  const MONTH_NUM_WITH_2_DIGITS = 10;
  const setUncategorizedTransactionsList = useStore((state) => state.setUncategorizedTransactionsList);
  const setUncategorizedTransactionsData = useStore((state) => state.setUncategorizedTransactionsData);
  const docType = statementObj ? STATEMENTS : EXPENSES;
  const { customerId, externalId } = expenseObj ?? statementObj;
  const [imageUrl, setImageUrl] = useState<string>('');
  const navigate = useNavigate();
  const { data, isLoading, isSuccess, error, isError } = useDownloadDocQuery({ customerId, externalId, docType });

  const [categorizationNeeded, setCategorizationNeeded] = useState<boolean>(false);

  if (categorizationNeeded) setCategorizationNeeded(false);

  const expensesStringTileFormatter = (str: string) => {
    if (!str) {
      return '';
    }
    if (str.includes('Q')) {
      const strArray = str.split('-');
      if (strArray.length === 2 && strArray[0].length === 4 && strArray[1].length === 2) {
        switch (strArray[1]) {
          case 'Q1':
            return '1st : Jan - Mar';
          case 'Q2':
            return '2nd : Apr - May';
          case 'Q3':
            return '3rd : Jun - Aug';
          case 'Q4':
            return '4th : Sept - Dec';
          default:
            return '';
        }
      }
    }
    if (str.includes('Full Year') || str.includes('Yearly')) {
      return 'All year';
    }
    const start = str.substring(0, 3);
    const end = str.substring(str.length - 10).trim();
    return `${start} : ${end}`;
  };

  const handleCategorization = () => {
    const uncategorizedTransactionsData = {
      quarter: expenseObj ? expenseObj.title.substring(0, 3) : '',
      dates: expenseObj ? expenseObj.title.substring(expenseObj.title.indexOf('2'), expenseObj.title.length) : '',
      externalId: expenseObj?.externalId,
    };
    if (uncategorizedTransactions) {
      setUncategorizedTransactionsList(uncategorizedTransactions);
      setUncategorizedTransactionsData(uncategorizedTransactionsData);
    }
  };

  const getTimeFrame = () => ({
    startDate: expenseObj?.startDate,
    endDate: expenseObj?.endDate,
  });

  const handlePrint = () => {
    if (imageUrl && !isLoading && isSuccess) {
      const pdfDialog = window.open(imageUrl, '_blank');
      pdfDialog!.print();
    } else if (isError) {
      console.log(error);
    }
  };

  const handleViewPdf = () => {
    if (imageUrl && !isLoading && isSuccess) {
      window.open(imageUrl, '_blank');
    } else if (isError) {
      console.log(error);
    }
  };

  const getFileName = () => {
    if (docType === STATEMENTS && statementObj) {
      switch (statementObj.accountType) {
        case MAIN_ACCOUNT:
          return `Lili Monthly Statement ${statementObj.year}-${statementObj.month < MONTH_NUM_WITH_2_DIGITS ? `0${statementObj.month}` : `${statementObj.month}`}`;
        case SAVING_BUCKET:
          return `Lili Monthly Statement - Savings ${statementObj.year}-${statementObj.month < MONTH_NUM_WITH_2_DIGITS ? `0${statementObj.month}` : `${statementObj.month}`}`;
        case TAX_BUCKET:
          return `Lili Monthly Statement - Tax Bucket ${statementObj.year}-${statementObj.month < MONTH_NUM_WITH_2_DIGITS ? `0${statementObj.month}` : `${statementObj.month}`}`;
        case EMERGENCY_BUCKET:
          return `Lili Monthly Statement - Emergency Bucket ${statementObj.year}-${statementObj.month < MONTH_NUM_WITH_2_DIGITS ? `0${statementObj.month}` : `${statementObj.month}`}`;
        default:
      }
    }
    if (docType === EXPENSES && expenseObj) {
      if (expenseObj.title.includes('Full Year')) {
        return `Lili Expenses Report - Full Year ${expenseObj.title.substring(0, 4)}`;
      }
      const dotsIndex = expenseObj.title.indexOf(':');
      const months = expenseObj.title.substring(dotsIndex + 1);
      const titleStart = expenseObj.title.substring(0, dotsIndex);
      return `${titleStart} (${months.replaceAll(' ', '')})`;
    }
    return 'document';
  };

  const handleDownload = (url: string | undefined) => {
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = getFileName();
      a.click();
    }
  };

  const { data: pdfLink } = expenseObj
    ? useDownloadExpenseReport({
      customerId: expenseObj.customerId,
      externalId: expenseObj.externalId,
      fileType: 'PDF',
    })
    : { data: '' };

  const { data: xlsLink } = expenseObj
    ? useDownloadExpenseReport({
      customerId: expenseObj.customerId,
      externalId: expenseObj.externalId,
      fileType: 'XLS',
    })
    : { data: '' };

  useEffect(() => {
    if (isSuccess && data && !imageUrl) {
      setImageUrl(data);
    }
  }, [data]);

  return (
    <div
      className='flex flex-col border border-bw03 items-center rounded-lg w-[10.5rem] h-[8.125rem] bg-bw02 overflow-hidden'
      id={id}
    >
      <div
        id={`TileHandlePrint-${tileIndex}`}
        onClick={() => {
          if (!uncategorizedTransactions?.numOfTransactions) handleViewPdf();
        }}
        tabIndex={0}
        onKeyDown={() => {}}
        role='button'
        className='font-metric text-left w-full text-xs text-bw07 px-[1.142rem] mt-4'
      >
        {expenseObj ? expensesStringTileFormatter(expenseObj.title) : statementObj.statementTitle}
      </div>
      <div className='bg-bw03 w-[8.438rem] border border-bw03 border-solid my-2' />
      {uncategorizedTransactions && uncategorizedTransactions.numOfTransactions > 0 && expenseObj ? (
        <div className='flex flex-col justify-center items-center'>
          <div className='font-metric text-bw08 text-center text-xs mb-[4px] px-4'>
            {`Categorize your ${expensesStringTileFormatter(expenseObj.title)}`}
          </div>

          <Icon
            onClick={() => {
              handleCategorization();
              navigate(`/${VIEWS_MAP.CATEGORIZE}`, {
                state: {
                  ...getTimeFrame(),
                  timeFrameTitle: expenseObj?.title,
                  showHeader: true,
                  backCtaTitle: 'View Report',
                },
              });
            }}
            className='w-[24px] h-[24px] cursor-pointer'
            src={cards}
            iconId={expensesStringTileFormatter(expenseObj.title).split(' ')[0]}
          />
        </div>
      ) : (
        <>
          <div
            id={`TileHandleViewPdf-${tileIndex}`}
            tabIndex={0}
            onKeyDown={() => {}}
            role='button'
            onClick={handleViewPdf}
            className='w-[6.438rem] h-[1.938rem] rounded-md rounded-b-none'
          >
            <div id={`tile-document-${tileIndex}`} className='w-[6.375rem] h-[1.938rem]'>
              <PdfPeek />
            </div>
          </div>
          <div className='flex justify-center items-center gap-4 bg-bw00 border-t-[1px] border-t-bw03  w-[10.5rem] h-[2.813rem]'>
            {docType === EXPENSES ? (
              <>
                <Button
                  id={`handlePrint${expensesStringTileFormatter(expenseObj?.title || '').split(' ')[0]}`}
                  onClick={handlePrint}
                  className='w-[1.5rem] h-[1.5rem]'
                  childElement={<PrintIcon id='expenses-handlePrint' className='text-bw07' />}
                />
                <Button
                  id={`handleExelDownload${expensesStringTileFormatter(expenseObj?.title || '').split(' ')[0]}`}
                  onClick={() => {
                    handleDownload(xlsLink);
                  }}
                  className='w-[1.5rem] h-[1.5rem]'
                  childElement={<ExcelIcon id='expenses-handleExelDownload' className='text-bw07' />}
                />
                <Button
                  id={`handlePdfDownload${expensesStringTileFormatter(expenseObj?.title || '').split(' ')[0]}`}
                  onClick={() => {
                    handleDownload(pdfLink);
                  }}
                  className='w-[1.5rem] h-[1.5rem]'
                  childElement={<PDFIcon id='expenses-handlePdfDownload' className='text-bw07' />}
                />
              </>
            ) : (
              <>
                <Button
                  id='handlePrint'
                  onClick={handlePrint}
                  className='w-[1.5rem] h-[1.5rem]'
                  childElement={<PrintIcon id='statements-handlePrint' className='text-bw07' />}
                />
                <Button
                  id='handleFileDownload'
                  onClick={() => {
                    handleDownload(imageUrl);
                  }}
                  className='w-[1.5rem] h-[1.5rem]'
                  childElement={<Icon iconId='statements-handleFileDownload' src={downloadIcon} />}
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
export default Tile;
